/**
 * Technicians list. Row click → detail drawer. Create → /service/technicians/new.
 * Supports crossLinkTechnician in sessionStorage (from ticket drawer).
 */
import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { useHistory } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import Alert from '@mui/material/Alert';
import Box from '@mui/material/Box';
import Button from '@mui/material/Button';
import Chip from '@mui/material/Chip';
import IconButton from '@mui/material/IconButton';
import Stack from '@mui/material/Stack';
import Tooltip from '@mui/material/Tooltip';
import { DataGrid, gridClasses } from '@mui/x-data-grid';
import AddIcon from '@mui/icons-material/Add';
import RefreshIcon from '@mui/icons-material/Refresh';
import * as api from '../../modules/api';
import PageContainer from '../mui-crud/components/PageContainer';
import useDataGridLocaleText from '../hooks/useDataGridLocaleText';
import { getCurrentRole } from '../utils/accessControl';
import ServiceTechnicianDetailDrawer from './ServiceTechnicianDetailDrawer';

const CROSS_LINK_KEY = 'crossLinkTechnician';

export default function ServiceTechniciansPage() {
  const { t } = useTranslation();
  const history = useHistory();
  const dataGridLocaleText = useDataGridLocaleText();
  const role = getCurrentRole();
  const canCreate = role === 'ADMIN' || role === 'SUPER_ADMIN';

  const [rows, setRows] = useState([]);
  const [rawById, setRawById] = useState({});
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [selected, setSelected] = useState(null);
  const [detailOpen, setDetailOpen] = useState(false);

  const load = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const { data } = await api.getServiceTechnicians();
      const list = Array.isArray(data) ? data : [];
      const map = {};
      list.forEach((p) => {
        if (p?.id) map[p.id] = p;
      });
      setRawById(map);
      setRows(list.map((p) => ({
        id: p.id,
        email: p.email,
        name: p.name || '—',
        phone: p.phone || '—',
        parentAdminEmail: p.parentAdminEmail,
        active: p.active !== false,
      })));
      return list;
    } catch (e) {
      setError(e?.response?.data?.message || e?.message || t('service.errorLoad'));
      setRows([]);
      setRawById({});
      return [];
    } finally {
      setLoading(false);
    }
  }, [t]);

  useEffect(() => {
    let cancelled = false;
    (async () => {
      const list = await load();
      if (cancelled) return;
      let email = '';
      try {
        email = (sessionStorage.getItem(CROSS_LINK_KEY) || '').trim();
        if (email) sessionStorage.removeItem(CROSS_LINK_KEY);
      } catch (_) { /* ignore */ }
      if (!email) return;
      const hit = list.find((p) => (p.email || '').toLowerCase() === email.toLowerCase());
      if (hit) {
        setSelected(hit);
        setDetailOpen(true);
      }
    })();
    return () => { cancelled = true; };
  }, [load]);

  const columns = useMemo(
    () => [
      { field: 'email', headerName: 'Email', flex: 1.2, minWidth: 180 },
      { field: 'name', headerName: t('service.techFullName'), flex: 1, minWidth: 160 },
      { field: 'phone', headerName: t('service.techPhone'), flex: 0.8, minWidth: 120 },
      {
        field: 'active',
        headerName: t('service.active'),
        width: 120,
        renderCell: (params) => (
          <Chip
            size="small"
            label={params.value ? t('service.activeYes') : t('service.activeNo')}
            color={params.value ? 'success' : 'default'}
            variant="outlined"
          />
        ),
      },
      { field: 'parentAdminEmail', headerName: t('service.partner'), flex: 1.2, minWidth: 180 },
    ],
    [t],
  );

  return (
    <PageContainer title={t('service.techniciansTitle')} breadcrumbs={[]}>
      <Stack spacing={2}>
        {error && <Alert severity="error">{error}</Alert>}
        <Stack direction="row" spacing={1} justifyContent="flex-end">
          <Tooltip title={t('common.reloadData')}>
            <IconButton onClick={load} disabled={loading}><RefreshIcon /></IconButton>
          </Tooltip>
          {canCreate && (
            <Button
              variant="contained"
              startIcon={<AddIcon />}
              onClick={() => history.push('/service/technicians/new')}
            >
              {t('service.createTech')}
            </Button>
          )}
        </Stack>
        <Box sx={{ height: 480, width: '100%' }}>
          <DataGrid
            rows={rows}
            columns={columns}
            loading={loading}
            localeText={dataGridLocaleText}
            disableRowSelectionOnClick
            onRowClick={(params) => {
              setSelected(rawById[params.id] || params.row);
              setDetailOpen(true);
            }}
            sx={{ [`& .${gridClasses.row}`]: { cursor: 'pointer' } }}
            pageSizeOptions={[20, 50]}
            initialState={{ pagination: { paginationModel: { pageSize: 20 } } }}
          />
        </Box>
      </Stack>

      <ServiceTechnicianDetailDrawer
        technician={selected}
        open={detailOpen}
        onClose={() => setDetailOpen(false)}
        onRefresh={load}
      />
    </PageContainer>
  );
}
